// Profit On Your Terms's Start here, worked out from the product's own rows (data.js: steps with
// their parent_step_id, and the member's step_progress with its answers; docs/data-model.md 10.6.2).
// Pure: no network, no React, so a test can import it as it is.
//
// - 2 rows (POYT.dc.html's "Deal analysis" and "Marketing setup": section start_here, type screens),
//   each holding its screens (type screen, parent_step_id the row), 19 in all, in sort_order.
// - The gate is the database's rule, mirrored here so the screen never offers what it would refuse:
//   a screen opens in order (the first, or 1 past the furthest done, across both rows), is done only
//   once it's answered, and stays done. A row is done once every screen in it is done (the database
//   marks it, private.screens_row_done).
// - A row's button reads Start with none of its screens touched, Continue part way, "Go back through
//   it" once done (catalogue.settings.start_here.buttons; Adam, 5 Oct), and opens the row's next
//   screen: the first not done, or its first screen once it's all done.
// - The way in without a row (/poyt/start-here) resumes at the first screen not done.
//
// Paths: /poyt/start-here/<row key>/<screen key>, e.g. /poyt/start-here/deal-analysis/s1-03-comp-exercise

export const BUTTONS = { start: 'Start', continue: 'Continue', done: 'Go back through it' }

const bySort = (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.id).localeCompare(String(b.id))
const isObject = v => v && typeof v === 'object' && !Array.isArray(v)

// The rows and screens in order, and the member's progress on each
export function startHereModel(data) {
  const steps = data?.steps || []
  const progress = new Map((data?.stepProgress || []).map(p => [p.step_id, p]))
  const rows = steps.filter(s => s.section === 'start_here' && !s.parent_step_id && s.step_type === 'screens').sort(bySort)
  const groups = rows.map(row => ({
    row,
    screens: steps.filter(s => s.step_type === 'screen' && s.parent_step_id === row.id).sort(bySort),
  }))
  const order = []
  for (const g of groups) for (const screen of g.screens) order.push({ screen, row: g.row, pos: order.length + 1 })
  const doneAt = id => progress.get(id)?.done_at || null
  const isDone = screen => Boolean(doneAt(screen.id))
  const furthest = order.reduce((m, o) => (isDone(o.screen) ? Math.max(m, o.pos) : m), 0)
  const doneCount = order.filter(o => isDone(o.screen)).length
  const resume = order.find(o => !isDone(o.screen)) || order[0] || null

  return {
    rows,
    groups,
    order,
    total: order.length,
    doneCount,
    furthest,
    resume,
    progressOf: id => progress.get(id) || null,
    answersOf: screen => {
      const a = progress.get(screen?.id)?.answers
      return isObject(a) ? a : {}
    },
    isDone,
    // the database's rule: the first screen, or 1 past the furthest done
    isOpen: entry => Boolean(entry) && entry.pos <= furthest + 1,
    find: (rowKey, screenKey) => order.find(o => o.row.step_key === rowKey && o.screen.step_key === screenKey) || null,
    rowDone: row => {
      if (doneAt(row.id)) return true
      const g = groups.find(x => x.row.id === row.id)
      return Boolean(g && g.screens.length && g.screens.every(isDone))
    },
    rowsDone: () => groups.filter(g => doneAt(g.row.id) || (g.screens.length && g.screens.every(isDone))).length,
  }
}

// The path of 1 screen
export function screenHref(product, row, screen) {
  return `${product?.path || ''}/start-here/${row.step_key}/${screen.step_key}`
}

// Where a row's button goes: its next screen, or its first once it's done
export function rowTarget(model, row) {
  const g = model.groups.find(x => x.row.id === row.id)
  if (!g || !g.screens.length) return null
  if (model.rowDone(row)) return model.order.find(o => o.screen.id === g.screens[0].id) || null
  const next = g.screens.find(s => !model.isDone(s)) || g.screens[0]
  return model.order.find(o => o.screen.id === next.id) || null
}

// Where an address that isn't a screen goes: a row's next screen, else where they're up to, else
// (no screens at all) the course's home
export function startHereTarget(product, model, rest) {
  const [rowKey, screenKey] = rest || []
  if (!model.order.length) return product?.path || '/'
  if (rowKey && screenKey && model.find(rowKey, screenKey)) return null
  const row = rowKey ? model.rows.find(r => r.step_key === rowKey) : null
  const to = (row && rowTarget(model, row)) || model.resume
  return screenHref(product, to.row, to.screen)
}

// Whether any screen in the row has been touched: done, or anything answered
export function rowTouched(model, row) {
  const g = model.groups.find(x => x.row.id === row.id)
  return Boolean(g && g.screens.some(s => model.isDone(s) || Object.keys(model.answersOf(s)).length > 0))
}

// The board's 2 rows, for the home's Start here card (POYT.dc.html): each with its title, line,
// label, whether it's done, how far through it they are, its button's words and where it goes.
//   startHereRows(product, data) -> [{ id, key, label, title, body, done, touched, screensDone,
//     screensTotal, button, href }]
export function startHereRows(product, data) {
  const model = startHereModel(data)
  const words = { ...BUTTONS, ...(isObject(product?.settings?.start_here?.buttons) ? product.settings.start_here.buttons : {}) }
  return model.groups.map(({ row, screens }) => {
    const done = model.rowDone(row)
    const touched = rowTouched(model, row)
    const target = rowTarget(model, row)
    return {
      id: row.id,
      key: row.step_key,
      label: row.config?.label || '',
      title: row.title,
      body: row.body_md || '',
      done,
      touched,
      screensDone: screens.filter(model.isDone).length,
      screensTotal: screens.length,
      button: done ? words.done : touched ? words.continue : words.start,
      href: target ? screenHref(product, target.row, target.screen) : `${product?.path || ''}/start-here`,
    }
  })
}

// "<k> of 2 done" (the rows) and "<n> of 19 screens done" (the screens)
export function startHereCount(data) {
  const model = startHereModel(data)
  return { rowsDone: model.rowsDone(), rows: model.groups.length, screensDone: model.doneCount, screens: model.total }
}

// ---- What a screen still needs before it can be marked done (live's answeredMissing, the same
// rule the database holds in private.merge_step_progress). config: the screen's; answers: theirs.
export function missingOn(config, answers) {
  const cfg = config || {}
  const d = isObject(answers) ? answers : {}
  const out = []
  switch (cfg.type) {
    case 'comp_filter':
    case 'sorter': {
      const a = isObject(d.answers) ? d.answers : {}
      if (!(cfg.cards || []).every(c => typeof a[c.id] === 'string')) out.push('cards')
      break
    }
    case 'walkthrough': if (d.walk_done !== true) out.push('walk'); break
    case 'calculator': if (d.calc_touched !== true) out.push('calc'); break
    case 'drill': {
      const hs = isObject(d.houses) ? d.houses : {}
      if (!(cfg.houses || []).every(h => hs[h.id]?.correct === true)) out.push('drill')
      break
    }
    case 'channels': {
      const op = isObject(d.chan_opened) ? d.chan_opened : {}
      if (!(cfg.channels || []).every((_, i) => op[String(i)] === true)) out.push('channels')
      break
    }
    case 'channel_choice': if (typeof d.secondary !== 'string' || !d.secondary) out.push('choice'); break
    case 'budget_mapper': if (typeof d.total !== 'number') out.push('budget'); break
    case 'gate': {
      const t = isObject(d.ticks) ? d.ticks : {}
      if (!(cfg.items || []).every((_, i) => t[String(i)] === true)) out.push('gate')
      break
    }
    default: break
  }
  if (cfg.quiz && !Number.isInteger(d.quiz?.chosen)) out.push('quiz')
  if (cfg.assignment && cfg.type !== 'gate') {
    const t = isObject(d.ticks) ? d.ticks : {}
    if (!(cfg.assignment.items || []).every((_, i) => t[String(i)] === true)) out.push('assignment')
  }
  return out
}

// A drill's house is right within £500 of the answer (the database's rule)
export function houseRight(house, entered) {
  return typeof entered === 'number' && Number.isFinite(entered) && Math.abs(entered - Number(house.answer)) <= 500
}

// What a member types for a house: "£108,000", "108000", "108 000"
export function parseAmount(raw) {
  const s = String(raw ?? '').replace(/[£,\s]/g, '')
  if (!/^-?\d+(\.\d+)?$/.test(s)) return null
  return parseFloat(s)
}

// ---- The letter budget (live's BudgetMapper)
export function budgetTotals({ batch, sends, cpl, secSpend }) {
  const letters = (Number(batch) || 0) * (Number(sends) || 0) * (Number(cpl) || 0)
  const total = letters + (Number(secSpend) || 0)
  return { letters, total, pct: Math.min(100, (total / 300) * 100) }
}

export function budgetVerdict({ letters, total }, secondary) {
  if (letters > 200) return 'lettersOver'
  if (total <= 200) return 'inside'
  if (secondary === 'ads') return 'ads'
  return 'over'
}

// ---- The data, after a save: the row the database gave back in place of theirs, and the row of
// screens marked done once its last screen is, as private.screens_row_done does
export function withProgress(data, saved) {
  if (!data || !saved?.step_id) return data
  const list = (data.stepProgress || []).filter(p => p.step_id !== saved.step_id)
  list.push({ position_seconds: 0, ...saved })
  let next = { ...data, stepProgress: list.sort((a, b) => String(a.step_id).localeCompare(String(b.step_id))) }
  const screen = (data.steps || []).find(s => s.id === saved.step_id)
  if (screen?.parent_step_id && saved.done_at) {
    const model = startHereModel(next)
    const row = model.rows.find(r => r.id === screen.parent_step_id)
    const rowProgress = model.progressOf(screen.parent_step_id)
    if (row && !rowProgress?.done_at && model.rowDone(row)) {
      const rest = next.stepProgress.filter(p => p.step_id !== row.id)
      rest.push({ step_id: row.id, position_seconds: 0, done_at: saved.done_at, answers: {}, updated_at: saved.updated_at || saved.done_at })
      next = { ...next, stepProgress: rest.sort((a, b) => String(a.step_id).localeCompare(String(b.step_id))) }
    }
  }
  return next
}

// ---- What the database's refusal means for the member (docs/data-model.md 10.6.2's lines).
// error: the Data API's { code, message, details, hint }, or a network failure (no code).
export function refusalKind(error, { viewingAs = false } = {}) {
  const code = error?.code || ''
  const message = String(error?.message || '')
  if (viewingAs) return 'viewing_as'
  if (!code) return 'connection'
  if (code === '42501' && message === 'screen_locked') return 'screen_locked'
  if (code === '23514' && message === 'screen_not_answered') {
    return ['cards', 'walk', 'calc', 'drill', 'channels', 'choice', 'budget', 'gate', 'quiz', 'assignment'].includes(error.details)
      ? error.details : 'fallback'
  }
  if (code === '42501' && (message === 'step_not_open' || /row-level security/i.test(message))) return 'step_not_open'
  return 'fallback'
}
